import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Activity, AlertTriangle, MessageCircle, Users, Clock, Eye, 
  Send, CheckCircle, XCircle, Loader2, RefreshCw, Bell, 
  TrendingUp, User, Phone, ChevronRight, Zap, Crown, Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Severity Badge
function SeverityBadge({ severity }) {
  const config = {
    critical: { color: 'bg-red-500 text-white', label: 'Critical' },
    high: { color: 'bg-orange-500 text-white', label: 'High' },
    medium: { color: 'bg-yellow-500 text-white', label: 'Medium' },
    low: { color: 'bg-blue-100 text-blue-800', label: 'Low' }
  };
  const c = config[severity] || config.low;
  return <Badge className={c.color}>{c.label}</Badge>;
}

// Active Session Card
function SessionCard({ session, onTakeover, onView }) {
  const lastMessage = session.messages?.[session.messages.length - 1];
  const messageCount = session.messages?.length || 0;
  
  return (
    <div 
      className={`p-3 sm:p-4 rounded-lg border transition-all ${
        session.flagged ? 'border-red-300 bg-red-50' : 
        session.status === 'owner_takeover' ? 'border-purple-300 bg-purple-50' :
        'border-gray-200 bg-white hover:border-gray-300'
      }`}
      data-testid={`session-${session.session_id}`}
    >
      <div className="flex items-start justify-between mb-2 sm:mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center ${
            session.status === 'owner_takeover' ? 'bg-purple-100' : 'bg-gray-100'
          }`}>
            {session.status === 'owner_takeover' ? (
              <Crown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600" />
            ) : (
              <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600" />
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900 text-sm">{session.user_name || 'Guest'}</p>
            <p className="text-xs text-gray-500">{messageCount} messages</p>
          </div>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-end">
          {session.flagged && <SeverityBadge severity={session.frustration_level || 'medium'} />}
          {session.status === 'owner_takeover' && (
            <Badge className="bg-purple-100 text-purple-800 text-xs">Live</Badge>
          )}
        </div>
      </div>
      
      {lastMessage && (
        <div className="p-2 bg-gray-100 rounded text-xs sm:text-sm text-gray-700 mb-2 sm:mb-3 line-clamp-2">
          <span className="font-medium">{lastMessage.role === 'user' ? 'Customer' : 'AI'}:</span> {lastMessage.content?.substring(0, 80)}...
        </div>
      )}
      
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-xs text-gray-500">
          <Clock className="w-3 h-3 inline mr-1" />
          {session.last_activity ? new Date(session.last_activity).toLocaleTimeString() : 'N/A'}
        </span>
        <div className="flex gap-1 sm:gap-2">
          <Button variant="ghost" size="sm" onClick={() => onView(session)} className="h-7 px-2 text-xs">
            <Eye className="w-3 h-3 mr-1" />
            View
          </Button>
          {session.status !== 'owner_takeover' && (
            <Button size="sm" onClick={() => onTakeover(session)} className="bg-purple-600 hover:bg-purple-700 h-7 px-2 text-xs">
              <MessageCircle className="w-3 h-3 mr-1" />
              Takeover
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Alert Card
function AlertCard({ alert, onAcknowledge, onViewSession }) {
  return (
    <div className={`p-3 sm:p-4 rounded-lg border ${
      alert.status === 'pending' ? 'border-red-300 bg-red-50 animate-pulse' : 'border-gray-200 bg-white'
    }`}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-2 gap-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className={`w-4 h-4 sm:w-5 sm:h-5 ${alert.status === 'pending' ? 'text-red-600' : 'text-gray-400'}`} />
          <div>
            <p className="font-medium text-gray-900 text-sm">{alert.user_name}</p>
            <p className="text-xs text-gray-500">{new Date(alert.created_at).toLocaleString()}</p>
          </div>
        </div>
        <SeverityBadge severity={alert.severity} />
      </div>
      
      <p className="text-xs sm:text-sm text-gray-700 mb-2">{alert.reason}</p>
      
      {alert.last_messages?.length > 0 && (
        <div className="text-xs text-gray-600 bg-white p-2 rounded border mb-2 sm:mb-3 line-clamp-2">
          Last message: "{alert.last_messages[alert.last_messages.length - 1]?.content?.substring(0, 80)}..."
        </div>
      )}
      
      <div className="flex gap-2 flex-wrap">
        {alert.status === 'pending' && (
          <Button size="sm" variant="outline" onClick={() => onAcknowledge(alert.alert_id)} className="h-8 text-xs px-2">
            <CheckCircle className="w-3 h-3 mr-1" />
            Acknowledge
          </Button>
        )}
        <Button size="sm" onClick={() => onViewSession(alert.session_id)} className="h-8 text-xs px-2">
          <Eye className="w-3 h-3 mr-1" />
          View Chat
        </Button>
      </div>
    </div>
  );
}

// Live Chat Modal for Takeover
function LiveChatModal({ isOpen, onClose, session, onSendMessage, onTakeover }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [takingOver, setTakingOver] = useState(false);

  const isLive = session?.status === 'owner_takeover';

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await onSendMessage(session.session_id, message);
      setMessage('');
    } finally {
      setSending(false);
    }
  };

  const handleJoinChat = async () => {
    setTakingOver(true);
    try {
      await onTakeover(session);
    } finally {
      setTakingOver(false);
    }
  };

  if (!session) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] sm:max-h-[80vh] flex flex-col p-3 sm:p-6">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex flex-wrap items-center gap-2 text-sm sm:text-base">
            <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
            <span className="hidden sm:inline">Live Support:</span> {session.user_name || 'Guest'}
            {session.flagged && <SeverityBadge severity={session.frustration_level} />}
            {isLive && <Badge className="bg-green-500 text-white text-xs">Live</Badge>}
          </DialogTitle>
        </DialogHeader>
        
        {/* Join Chat Banner - Show if not yet taken over */}
        {!isLive && (
          <div className="p-3 sm:p-4 bg-purple-50 border border-purple-200 rounded-lg mb-2">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
              <div>
                <p className="font-semibold text-purple-900 text-sm">Customer needs assistance</p>
                <p className="text-xs sm:text-sm text-purple-700">Join to help directly</p>
              </div>
              <Button 
                onClick={handleJoinChat} 
                disabled={takingOver}
                className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto h-9 text-sm"
              >
                {takingOver ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <MessageCircle className="w-4 h-4 mr-1" />
                )}
                Join Chat
              </Button>
            </div>
          </div>
        )}
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-2 sm:space-y-3 p-2 sm:p-4 bg-gray-50 rounded-lg max-h-[40vh] sm:max-h-96">
          {session.messages?.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}>
              <div className={`max-w-[85%] sm:max-w-[80%] p-2 sm:p-3 rounded-lg ${
                msg.role === 'user' ? 'bg-white border' :
                msg.role === 'admin' ? 'bg-purple-600 text-white' :
                'bg-blue-100 text-blue-900'
              }`}>
                <div className="flex items-center gap-1 sm:gap-2 mb-1">
                  {msg.role === 'user' ? (
                    <User className="w-3 h-3" />
                  ) : msg.role === 'admin' ? (
                    <Crown className="w-3 h-3" />
                  ) : (
                    <Zap className="w-3 h-3" />
                  )}
                  <span className="text-xs font-medium">
                    {msg.role === 'user' ? 'Customer' : msg.role === 'admin' ? msg.admin_name || 'Support' : 'AI'}
                  </span>
                  <span className="text-xs opacity-60">
                    {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : ''}
                  </span>
                </div>
                <p className="text-xs sm:text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Input - Only show if session is taken over */}
        {isLive ? (
          <div className="flex gap-2 pt-3 sm:pt-4">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 min-h-[50px] sm:min-h-[60px] text-sm"
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
            />
            <Button onClick={handleSend} disabled={sending || !message.trim()} className="bg-purple-600 hover:bg-purple-700 h-auto px-3">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        ) : (
          <div className="pt-3 sm:pt-4 text-center text-gray-500 text-xs sm:text-sm">
            <p>Click "Join Chat" to start responding</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Main AI Performance Monitor
export default function AIPerformanceMonitor({ initialSessionId }) {
  const { getAuthHeaders } = useAuth();
  const [activeSessions, setActiveSessions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showLiveChat, setShowLiveChat] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [initialSessionLoaded, setInitialSessionLoaded] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [sessionsRes, alertsRes] = await Promise.all([
        axios.get(`${API}/support/sessions/active`, { headers: getAuthHeaders(), withCredentials: true }).catch(() => ({ data: { sessions: [] } })),
        axios.get(`${API}/support/alerts`, { headers: getAuthHeaders(), withCredentials: true }).catch(() => ({ data: { alerts: [] } }))
      ]);
      
      setActiveSessions(sessionsRes.data?.sessions || []);
      setAlerts(alertsRes.data?.alerts || []);
    } catch (error) {
      console.error('Failed to fetch AI performance data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Auto-load initial session from URL parameter
  useEffect(() => {
    if (initialSessionId && !initialSessionLoaded && !loading) {
      setInitialSessionLoaded(true);
      handleViewSession(initialSessionId);
    }
  }, [initialSessionId, initialSessionLoaded, loading]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleTakeover = async (session) => {
    try {
      await axios.post(`${API}/support/takeover`, {
        session_id: session.session_id,
        message: "Hi, I'm a live support agent. How can I help you today?"
      }, { headers: getAuthHeaders(), withCredentials: true });
      
      toast.success('Takeover successful! You can now message the customer.');
      
      // Refresh the session data to get updated status
      const response = await axios.get(`${API}/support/sessions/${session.session_id}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      // Update with fresh session data that has status='owner_takeover'
      setSelectedSession(response.data?.session);
      setShowLiveChat(true);
      fetchData();
    } catch (error) {
      toast.error('Failed to take over session');
    }
  };

  const handleSendMessage = async (sessionId, message) => {
    try {
      await axios.post(`${API}/support/sessions/${sessionId}/message`, 
        { message },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      // Refresh session data
      const response = await axios.get(`${API}/support/sessions/${sessionId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setSelectedSession(response.data?.session);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await axios.post(`${API}/support/alerts/${alertId}/acknowledge`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Alert acknowledged');
      fetchData();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleViewSession = async (sessionId) => {
    if (!sessionId) {
      toast.error('Invalid session ID');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/support/sessions/${sessionId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      if (response.data?.session) {
        setSelectedSession(response.data.session);
        setShowLiveChat(true);
      } else {
        toast.error('Session not found or has ended');
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      if (error.response?.status === 404) {
        toast.error('Session not found. It may have ended or been deleted.');
      } else {
        toast.error('Failed to load session. Please try again.');
      }
    }
  };

  const pendingAlerts = alerts.filter(a => a.status === 'pending');
  const flaggedSessions = activeSessions.filter(s => s.flagged);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="ai-performance-monitor">
      {/* Header Stats - Mobile Optimized */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
        <Card className={pendingAlerts.length > 0 ? 'border-red-300 bg-red-50' : ''}>
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center ${
                pendingAlerts.length > 0 ? 'bg-red-500 animate-pulse' : 'bg-gray-100'
              }`}>
                <AlertTriangle className={`w-4 h-4 sm:w-5 sm:h-5 ${pendingAlerts.length > 0 ? 'text-white' : 'text-gray-600'}`} />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold">{pendingAlerts.length}</p>
                <p className="text-xs text-gray-500">Alerts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold">{activeSessions.length}</p>
                <p className="text-xs text-gray-500">Chats</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold">{flaggedSessions.length}</p>
                <p className="text-xs text-gray-500">Flagged</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-3 sm:pt-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold">
                  {activeSessions.filter(s => s.status === 'owner_takeover').length}
                </p>
                <p className="text-xs text-gray-500">Live</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts Section */}
      {pendingAlerts.length > 0 && (
        <Card className="border-red-300">
          <CardHeader className="pb-2 sm:pb-3">
            <CardTitle className="flex items-center gap-2 text-red-700 text-sm sm:text-base">
              <Bell className="w-4 h-4 sm:w-5 sm:h-5 animate-bounce" />
              <span className="hidden sm:inline">Customer Frustration Alerts</span>
              <span className="sm:hidden">Frustration Alerts</span>
              <Badge className="bg-red-500 text-white ml-2 text-xs">{pendingAlerts.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 sm:space-y-3">
            {pendingAlerts.map(alert => (
              <AlertCard 
                key={alert.alert_id}
                alert={alert}
                onAcknowledge={handleAcknowledge}
                onViewSession={handleViewSession}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Active Sessions */}
      <Card>
        <CardHeader className="pb-2 sm:pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                Live Support Sessions
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">Monitor and take over conversations</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="h-8">
              <RefreshCw className={`w-4 h-4 mr-1 sm:mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {activeSessions.length === 0 ? (
            <div className="text-center py-8 sm:py-12 text-gray-500">
              <MessageCircle className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 text-gray-300" />
              <p className="text-sm sm:text-base">No active support sessions</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              {activeSessions.map(session => (
                <SessionCard
                  key={session.session_id}
                  session={session}
                  onTakeover={handleTakeover}
                  onView={handleViewSession}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Live Chat Modal */}
      <LiveChatModal
        isOpen={showLiveChat}
        onClose={() => { setShowLiveChat(false); setSelectedSession(null); }}
        session={selectedSession}
        onSendMessage={handleSendMessage}
        onTakeover={handleTakeover}
      />
    </div>
  );
}
